import {
    Component,
    h,
    Prop,
    State,
} from '@stencil/core';
import { ServiceType } from '@uipath/portal-shell-util';

import { EcommerceEvent } from '../../models/appInsights/events/ecommerce.events';
import { ITrialUpgradeDto } from '../../models/upgradeToTrial';
import store from '../../store/store';
import { accountAndTenantsStore } from '../../utils/accountAndTenants/accountAndTenants';
import { AccountAndTenantsInstanceResolver } from '../../utils/accountAndTenants/accountAndTenantsInstanceResolver';
import { upgradeToTrial } from '../../utils/api/upgradeToTrial';
import { getLanguage } from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { getOrganizationStandardUrl } from '../../utils/orgStandardUrlHelper';
import {
    getAccessToken,
    sessionStore,
} from '../../utils/session/sessionStore';

@Component({
    tag: 'ap-upgrade-trial-internal',
    styleUrl: 'ap-upgrade-trial-internal.scss',
    shadow: true,
})
export class ApUpgradeTrialInternal {

    /**
     * Dialog reference
     */
    @Prop() dialogRef: HTMLApDialogElement;

    @State() email: string = sessionStore.get('email');

    @State() companyName?: string = accountAndTenantsStore.get('organization')?.name;

    @State() role: string;

    render() {

        const organizationStandardUrl: string = getOrganizationStandardUrl();

        const classes = {
            'portal-button': true,
            'primary': true,
            'md': true,
        };

        return (
            <div class="main-container">
                <div class="info-panel">
                    <h3 class="start-trial-header">{t('start-free-trial', getLanguage(store))}</h3>
                    <p class="start-trial-description">{t('take-advantage-capabilities')}</p>
                    <div class="centered-bullet-points">
                        <portal-icon name="timer" size="24px" class="description-icons"/>
                        <p class="description-text">{t('capability-one')}</p>
                        <portal-icon name="lock" size="24px" class="description-icons"/>
                        <p class="description-text">{t('capability-two')}</p>
                        <portal-icon name="security" size="24px" class="description-icons"/>
                        <p class="description-text">{t('capability-three')}</p>
                        <p class="description-text spacing-top">{t('upgrade-to-enterprise')}</p>
                        <portal-button
                            label={t('contact-enterprise-sales')}
                            onClick={() => Logger.logCustomEvent(EcommerceEvent.ClickContactSales)}
                        />
                    </div>
                </div>
                <div class="start-trial-panel">
                    <form onSubmit={(e) => this.handleSubmit(e, organizationStandardUrl)}>
                        <label>{t('upgrade-trial-business-email')}</label>
                        <input
                            class="text-input"
                            required
                            type="text"
                            name="email"
                            id="input-email"
                            value={this.email}
                            onInput={(event) => {
                                this.email = (event.target as HTMLInputElement).value;
                            }}/>
                        <label>{t('upgrade-trial-company-name')}</label>
                        <input
                            class="text-input"
                            required
                            type="text"
                            name="company-name"
                            id="input-company-name"
                            value={this.companyName}
                            onInput={(event) => {
                                this.companyName = (event.target as HTMLInputElement).value;
                            }}/>
                        <label>{t('upgrade-trial-role')}</label>
                        <input
                            class="text-input"
                            required
                            type="text"
                            name="role"
                            id="input-role"
                            onInput={(event) => {
                                this.role = (event.target as HTMLInputElement).value;
                            }}/>
                        <button class={classes} type='submit' >Start free trial</button>
                    </form>
                    <portal-button
                        class="pricing-button"
                        label={t('view-pricing')}
                        href={`${organizationStandardUrl}/${ServiceType.cloudRpa}_/buypro`}
                        onClick={() => Logger.logCustomEvent(EcommerceEvent.ClickViewPricing)}
                    />
                </div>
            </div>
        );
    }

    private async handleSubmit(e: Event, organizationStandardUrl: string) {

        e.preventDefault();

        const organization = accountAndTenantsStore.get('organization');
        const accessToken = getAccessToken();
        const accountUserId = sessionStore.get('userId');
        const accountGlobalId = sessionStore.get('organizationId');
        const accountCountry = organization?.country ?? '';
        const nameArray = this.getName();

        Logger.logCustomEvent(EcommerceEvent.ClickStartFreeTrial, {
            invocationMethod: 'ProfileMenuUpgradeTrialInternalClickStartFreeTrial',
            subscriptionType: organization?.accountType,
        });

        const payload: ITrialUpgradeDto = {
            organizationName: this.companyName,
            firstName: nameArray[0],
            lastName: nameArray[1],
            email: this.email,
            jobTitle: this.role,
            country: accountCountry,
            state: '',
            businessEmail: this.email,
            userId: accountUserId,
            accountUrl: organizationStandardUrl,
            userLanguage: getLanguage(store),
            accountCreatedDate: '',
        };

        await upgradeToTrial(organizationStandardUrl, accessToken, accountUserId, accountGlobalId, payload);

        this.dialogRef.toggleDialog();

        await AccountAndTenantsInstanceResolver.Instance?.fetchAccountAndTenants(true);
    }

    private getName() {
        const nameArray = sessionStore.get('fullName').split(' ');
        if (nameArray.length < 2) {
            return [ nameArray[0], '' ];
        }
        return [ nameArray[0], nameArray[1] ];
    }
}
